import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
    MessageCircle, 
    X, 
    Send, 
    Loader2, 
    Bot,
    User,
    Phone,
    Minimize2,
    Maximize2,
    GripVertical
} from 'lucide-react';
import api from '../../lib/api';
import { trackChatbotInteraction } from '../../lib/metaPixel';

const WHATSAPP_NUMBER = '+447301893140';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([
        {
            type: 'bot',
            text: "Hi! I'm the RecruitNearMe assistant. I can help you with:\n\n• Finding recruiters near you\n• Our Multi-Apply service\n• Pricing & subscriptions\n• General questions\n\nHow can I help you today?",
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const messagesEndRef = useRef(null);
    
    // Draggable state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef(null);
    const dragStartPos = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Generate or retrieve session ID
    useEffect(() => {
        const storedSessionId = localStorage.getItem('chatbot_session_id');
        if (storedSessionId) {
            setSessionId(storedSessionId);
        } else {
            const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('chatbot_session_id', newSessionId);
            setSessionId(newSessionId);
        }
    }, []);

    // Drag handlers
    const handleDragStart = (e) => {
        if (e.target.closest('button') || e.target.closest('input')) return;
        
        setIsDragging(true);
        const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
        
        dragStartPos.current = {
            x: clientX,
            y: clientY,
            posX: position.x,
            posY: position.y
        };
    };

    const handleDrag = (e) => {
        if (!isDragging) return;
        
        const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
        
        const deltaX = dragStartPos.current.x - clientX;
        const deltaY = dragStartPos.current.y - clientY;
        
        // Calculate new position (inverted because we're using bottom/right positioning)
        const newX = Math.max(0, Math.min(window.innerWidth - 400, dragStartPos.current.posX + deltaX));
        const newY = Math.max(0, Math.min(window.innerHeight - 500, dragStartPos.current.posY + deltaY));
        
        setPosition({ x: newX, y: newY });
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleDrag);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleDrag);
            window.addEventListener('touchend', handleDragEnd);
            
            return () => {
                window.removeEventListener('mousemove', handleDrag);
                window.removeEventListener('mouseup', handleDragEnd);
                window.removeEventListener('touchmove', handleDrag);
                window.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging]);

    const sendMessage = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        
        // Add user message to chat
        setMessages(prev => [...prev, {
            type: 'user',
            text: userMessage,
            timestamp: new Date()
        }]);

        setIsLoading(true);

        try {
            const response = await api.post('/chatbot/message', {
                message: userMessage,
                session_id: sessionId
            });

            // Add bot response
            setMessages(prev => [...prev, {
                type: 'bot',
                text: response.data.response,
                timestamp: new Date()
            }]);

            // Update session ID if returned
            if (response.data.session_id) {
                setSessionId(response.data.session_id);
                localStorage.setItem('chatbot_session_id', response.data.session_id);
            }
        } catch (error) {
            console.error('Chatbot error:', error);
            setMessages(prev => [...prev, {
                type: 'bot',
                text: "I'm sorry, I'm having trouble responding right now. Please try again or chat with our team on WhatsApp.",
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const openWhatsApp = () => {
        const message = encodeURIComponent("Hi! I'd like to speak with someone about RecruitNearMe services.");
        window.open(`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${message}`, '_blank');
    };

    const formatTime = (date) => {
        return new Date(date).toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => {
                    setIsOpen(true);
                    trackChatbotInteraction('opened');
                }}
                style={{ 
                    right: `${24 + position.x}px`, 
                    bottom: `${24 + position.y}px` 
                }}
                className="fixed z-50 w-14 h-14 bg-primary-600 hover:bg-primary-700 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 group"
                aria-label="Open chat"
            >
                <MessageCircle className="w-6 h-6 text-white" />
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold animate-pulse">
                    1
                </span>
            </button>
        );
    }

    return (
        <div 
            ref={dragRef}
            style={{ 
                right: `${24 + position.x}px`, 
                bottom: `${24 + position.y}px` 
            }}
            className={`fixed z-50 ${isMinimized ? 'w-80' : 'w-96'} bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden transition-all ${isDragging ? 'cursor-grabbing' : ''}`}
        >
            {/* Header - Draggable Area */}
            <div 
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4 cursor-grab active:cursor-grabbing select-none"
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <Bot className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold flex items-center gap-2">
                                RecruitNearMe
                                <GripVertical className="w-4 h-4 opacity-50" />
                            </h3>
                            <p className="text-xs text-primary-100">Online • Drag to move</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setIsMinimized(!isMinimized)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {!isMinimized && (
                <>
                    {/* Messages */}
                    <div className="h-80 overflow-y-auto p-4 space-y-4 bg-neutral-50">
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`flex items-end gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                                        msg.type === 'user' 
                                            ? 'bg-primary-600' 
                                            : msg.isError ? 'bg-red-100' : 'bg-primary-100'
                                    }`}>
                                        {msg.type === 'user' ? (
                                            <User className="w-4 h-4 text-white" />
                                        ) : (
                                            <Bot className={`w-4 h-4 ${msg.isError ? 'text-red-600' : 'text-primary-600'}`} />
                                        )}
                                    </div>
                                    <div className={`rounded-2xl px-4 py-2 ${
                                        msg.type === 'user'
                                            ? 'bg-primary-600 text-white rounded-br-sm'
                                            : msg.isError 
                                                ? 'bg-red-50 text-red-700 rounded-bl-sm border border-red-200'
                                                : 'bg-white text-neutral-800 rounded-bl-sm shadow-sm border border-neutral-100'
                                    }`}>
                                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                                        <p className={`text-xs mt-1 ${
                                            msg.type === 'user' ? 'text-primary-200' : 'text-neutral-400'
                                        }`}>
                                            {formatTime(msg.timestamp)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="flex items-end gap-2">
                                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-primary-600" />
                                    </div>
                                    <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-neutral-100">
                                        <div className="flex items-center gap-1">
                                            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>

                    {/* WhatsApp Banner */}
                    <div className="px-4 py-2 bg-green-50 border-t border-green-100">
                        <button
                            onClick={openWhatsApp}
                            className="w-full flex items-center justify-center gap-2 text-sm text-green-700 hover:text-green-800 font-medium"
                        >
                            <Phone className="w-4 h-4" />
                            Chat with a real person on WhatsApp
                        </button>
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t bg-white">
                        <div className="flex items-center gap-2">
                            <Input
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your message..."
                                className="flex-1"
                                disabled={isLoading}
                            />
                            <Button
                                onClick={sendMessage}
                                disabled={!inputValue.trim() || isLoading}
                                className="bg-primary-600 hover:bg-primary-700 shrink-0"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Chatbot;
